import React, { useState, useEffect } from 'react';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  Phone,
  Mail,
  MessageSquare,
  Check,
  X,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { reservationAPI } from '../lib/api';
import { formatDate, formatTime, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const ReservationsPage = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);

  const fetchReservations = async () => {
    try {
      const status = statusFilter === 'all' ? null : statusFilter;
      const date = dateFilter ? format(dateFilter, 'yyyy-MM-dd') : null;
      const response = await reservationAPI.getAll(status, date);
      setReservations(response.data);
    } catch (error) {
      toast.error('Failed to fetch reservations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, [statusFilter, dateFilter]);

  const updateStatus = async (reservationId, newStatus) => {
    try {
      await reservationAPI.updateStatus(reservationId, newStatus);
      toast.success(`Reservation ${newStatus}`);
      fetchReservations();
    } catch (error) {
      toast.error('Failed to update reservation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reservations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Reservations</h1>
          <p className="text-gray-500">Manage table bookings and availability</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="date-filter">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {dateFilter ? format(dateFilter, 'MMM dd') : 'Pick Date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={dateFilter}
                onSelect={setDateFilter}
                initialFocus
              />
              {dateFilter && (
                <div className="p-2 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full"
                    onClick={() => setDateFilter(null)}
                  >
                    Clear filter
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Reservations List */}
      {reservations.length === 0 ? (
        <div className="card-dashboard text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CalendarIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">No reservations</h3>
          <p className="text-gray-500">Reservations will appear here when customers book tables</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reservations.map((reservation) => (
            <div 
              key={reservation.id} 
              className="card-dashboard"
              data-testid={`reservation-card-${reservation.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D8F3DC] rounded-xl flex items-center justify-center">
                    <CalendarIcon className="w-6 h-6 text-[#1A4D2E]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900">{reservation.customer_name}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        {formatDate(reservation.reservation_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatTime(reservation.reservation_time)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {reservation.party_size} guests
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {reservation.customer_phone}
                      </span>
                      {reservation.customer_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {reservation.customer_email}
                        </span>
                      )}
                    </div>
                    {reservation.special_requests && (
                      <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <MessageSquare className="w-4 h-4 text-yellow-600 mt-0.5" />
                          <p className="text-sm text-yellow-800">{reservation.special_requests}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(reservation.status)}`}>
                    {reservation.status}
                  </span>
                  
                  {reservation.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => updateStatus(reservation.id, 'cancelled')}
                        data-testid={`cancel-reservation-${reservation.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        className="btn-primary"
                        onClick={() => updateStatus(reservation.id, 'confirmed')}
                        data-testid={`confirm-reservation-${reservation.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Confirm
                      </Button>
                    </div>
                  )}
                  
                  {reservation.status === 'confirmed' && (
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => updateStatus(reservation.id, 'completed')}
                      data-testid={`complete-reservation-${reservation.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Complete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
